import { useState, useRef, useEffect } from "react";
import { useTheme } from "./ThemeProvider";

/**
 * Botão de tema com dropdown — mostra ícone do tema atual, ao clicar
 * abre seletor com os 3 disponíveis.
 */
export function ThemeToggle() {
  const { theme, setTheme, themes } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded hover:bg-app-surface-2 transition"
        aria-label="Mudar tema"
        title="Mudar tema"
      >
        {theme === "light" && <SunIcon />}
        {theme === "dark" && <MoonIcon />}
        {theme === "midnight" && <SparklesIcon />}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-app-surface border border-app-border rounded-lg shadow-lg p-1 z-50">
          {themes.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => { setTheme(t.key); setOpen(false); }}
              className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 transition ${
                t.key === theme
                  ? "bg-app-accent text-app-accent-text"
                  : "hover:bg-app-surface-2"
              }`}
            >
              <span className="w-4">
                {t.key === "light" ? <SunIcon /> : t.key === "dark" ? <MoonIcon /> : <SparklesIcon />}
              </span>
              <span className="flex-1">
                <p className="font-medium">{t.label}</p>
                <p className={`text-xs ${t.key === theme ? "opacity-80" : "text-app-muted"}`}>
                  {t.description}
                </p>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/>
      <path d="M19 14l.75 2.25L22 17l-2.25.75L19 20l-.75-2.25L16 17l2.25-.75L19 14z"/>
    </svg>
  );
}
